import { cn } from '@/lib/cn';
import { MARK_PATHS, MARK_STROKE_WIDTH } from './logoPaths';

/**
 * The Voltra mark and lockup.
 *
 * The mark is inlined rather than loaded from `/logo-mark.svg` for one reason:
 * `currentColor`. An <img> is an opaque document — it cannot inherit the ink
 * colour of whatever it sits on, so the same file could not serve the green
 * brand mark, a white knockout on a coloured panel, and a muted disabled
 * state. Inlined, it is 400 bytes of markup with no request.
 *
 * The public file still exists for the favicon and the manifest, which need a
 * URL. `Logo.test.tsx` asserts the two carry identical path data, because two
 * copies of a shape drift silently and you find out from a customer.
 */

export function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={cn('shrink-0', className)}
      fill="none"
      aria-hidden
      focusable="false"
    >
      <g stroke="currentColor" strokeWidth={MARK_STROKE_WIDTH} strokeLinejoin="round">
        <path d={MARK_PATHS.ring} />
        <path d={MARK_PATHS.cap} />
        <path d={MARK_PATHS.v} />
      </g>
    </svg>
  );
}

/**
 * Mark plus wordmark.
 *
 * "POS" is set lighter than "Voltra" because they are different things — the
 * company and the product. The supplied brand asset is the company wordmark
 * alone; this is the product lockup built from it.
 */
export function Logo({
  className,
  markClassName = 'h-8 w-8',
  wordmarkClassName = 'text-[15px]',
  wordmark = true,
}: {
  className?: string;
  markClassName?: string;
  wordmarkClassName?: string;
  wordmark?: boolean;
}) {
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <LogoMark className={cn('text-logo', markClassName)} />
      {wordmark ? (
        // No colour of its own: it inherits, so the same lockup works on the
        // light auth page and on the black sidebar without a variant prop.
        <span className={cn('truncate font-semibold tracking-tight', wordmarkClassName)}>
          Voltra <span className="font-medium text-ink-muted">POS</span>
        </span>
      ) : (
        // Only when there is no visible wordmark — otherwise a screen reader
        // announces the name twice.
        <span className="sr-only">Voltra POS</span>
      )}
    </span>
  );
}
